import { useCallback, useMemo } from 'react';
import { graphql, useFragment, useRelayEnvironment, fetchQuery } from 'react-relay';
import { useSbSelector } from '../../reducers/useSbSelector';
import { type usePartialItems_item$key } from './__generated__/usePartialItems_item.graphql';

/* eslint-disable relay/unused-fields */
const partialItemsQuery = graphql`
    query usePartialItemsQuery(
        $itemIds: [String]!
        $personalizationTypes: [String]!
        $isTrade: Boolean!
        $fetchTileVideo: Boolean!
        $showSeller: Boolean!
        $priceBookName: String = ""
        $pageDisplayEnum: PageDisplayEnum = searchAndBrowse
        $userCountryCode: String = ""
    ) {
        viewer {
            itemSearchInflateItems(itemIds: $itemIds, personalizationTypes: $personalizationTypes) {
                edges {
                    node {
                        item {
                            serviceId
                            isPartialItem
                            ...FavoritesProvider_item
                            ...useSharedUrgencySignals_item
                            ...useItemShippingPrices_item
                            ...useSbSharedItemTracking_item
                            ...SharedItemTile_item
                                @arguments(
                                    isTrade: $isTrade
                                    fetchVideo: $fetchTileVideo
                                    priceBookName: $priceBookName
                                    showSeller: $showSeller
                                )
                        }
                    }
                }
            }
        }
    }
`;
/* eslint-enable relay/unused-fields */

const itemFragment = graphql`
    fragment usePartialItems_item on Item @relay(plural: true) {
        serviceId
        isPartialItem
        personalizationType
    }
`;

export const usePartialItems = ({
    items: itemRef,
    showSellerName = false,
}: {
    items: usePartialItems_item$key;
    showSellerName?: boolean;
}): {
    fetchPartialItems: () => Promise<void>;
    partialItemsIds: string[];
} => {
    const items = useFragment(itemFragment, itemRef);
    const environment = useRelayEnvironment();
    const {
        numOfItemsToInflate,
        isTrade,
        fetchTileVideo = false,
        userCountryCode,
        priceBookName,
    } = useSbSelector(state => state.relayVariables.variables);
    const { itemIds, personalizationTypes } = useMemo(() => {
        const ids: string[] = [];
        const types: string[] = [];

        if (numOfItemsToInflate) {
            items.forEach(item => {
                if (item?.isPartialItem && item?.serviceId) {
                    ids.push(item.serviceId);
                    types.push(item.personalizationType || '');
                }
            });
        }

        // personalizationTypes are coming from item-search response in root call, as I'm inflating data just from itemQuery here, need to send these around to have correct/full data in Relay.
        return {
            itemIds: ids,
            personalizationTypes: types,
        };
    }, [items, numOfItemsToInflate]);

    const fetchPartialItems = useCallback(async (): Promise<void> => {
        if (!itemIds.length) {
            return;
        }
        // TODO: React 19 startTransition will support async functions
        // startTransition(async () => {
        await fetchQuery(environment, partialItemsQuery, {
            itemIds,
            personalizationTypes,
            showSeller: showSellerName,
            pageDisplayEnum: 'searchAndBrowse',
            isTrade,
            fetchTileVideo,
            userCountryCode,
            priceBookName,
        }).toPromise();
        // });
    }, [
        environment,
        showSellerName,
        isTrade,
        fetchTileVideo,
        userCountryCode,
        priceBookName,
        itemIds,
        personalizationTypes,
    ]);

    return { fetchPartialItems, partialItemsIds: itemIds };
};
